/**
 * Workflow State Machine
 * Manages all state transitions for the Athena TARA workflow
 */

import { WORKFLOW_STATES, ERROR_CODES } from '../../../config/athenaWorkflowConfig';

export class WorkflowStateMachine {
  constructor(initialState = WORKFLOW_STATES.IDLE) {
    this.currentState = initialState;
    this.previousState = null;
    this.stateHistory = [initialState];
    this.context = {
      currentStep: 0,
      error: null,
      retryCount: 0,
      lastTransition: Date.now()
    };

    // Define valid state transitions
    this.transitions = {
      [WORKFLOW_STATES.IDLE]: [
        WORKFLOW_STATES.INITIALIZING,
        WORKFLOW_STATES.UPLOADING_DOCUMENT,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.PROCESSING,  // Allow direct transition for restoration
        WORKFLOW_STATES.REVIEWING    // Allow direct transition when restoring completed step
      ],
      [WORKFLOW_STATES.INITIALIZING]: [
        WORKFLOW_STATES.IDLE,
        WORKFLOW_STATES.UPLOADING_DOCUMENT,
        WORKFLOW_STATES.ERROR
      ],
      [WORKFLOW_STATES.UPLOADING_DOCUMENT]: [
        WORKFLOW_STATES.DOCUMENT_UPLOADED,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.IDLE
      ],
      [WORKFLOW_STATES.DOCUMENT_UPLOADED]: [
        WORKFLOW_STATES.STARTING_ANALYSIS,
        WORKFLOW_STATES.IDLE
      ],
      [WORKFLOW_STATES.STARTING_ANALYSIS]: [
        WORKFLOW_STATES.PROCESSING,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.PAUSED
      ],
      [WORKFLOW_STATES.PROCESSING]: [
        WORKFLOW_STATES.POLLING,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.RATE_LIMITED,
        WORKFLOW_STATES.TIMEOUT,
        WORKFLOW_STATES.PAUSED,
        WORKFLOW_STATES.REVIEWING,
        WORKFLOW_STATES.PROCESSING  // Allow staying in PROCESSING with context update
      ],
      // Add lowercase version for compatibility
      ['processing']: [
        WORKFLOW_STATES.POLLING,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.RATE_LIMITED,
        WORKFLOW_STATES.TIMEOUT,
        WORKFLOW_STATES.PAUSED,
        WORKFLOW_STATES.REVIEWING,
        'reviewing',
        'processing'
      ],
      [WORKFLOW_STATES.POLLING]: [
        WORKFLOW_STATES.REVIEWING,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.RATE_LIMITED,
        WORKFLOW_STATES.PROCESSING
      ],
      [WORKFLOW_STATES.REVIEWING]: [
        WORKFLOW_STATES.AWAITING_APPROVAL,
        WORKFLOW_STATES.REVISION_REQUESTED,
        WORKFLOW_STATES.APPROVED,
        WORKFLOW_STATES.PROCESSING,  // Allow going back to processing for retries/resume
        WORKFLOW_STATES.REVIEWING  // Allow staying in reviewing with context update
      ],
      // Add lowercase version for compatibility
      ['reviewing']: [
        WORKFLOW_STATES.AWAITING_APPROVAL,
        WORKFLOW_STATES.REVISION_REQUESTED,
        WORKFLOW_STATES.APPROVED,
        WORKFLOW_STATES.PROCESSING,
        'reviewing'
      ],
      [WORKFLOW_STATES.AWAITING_APPROVAL]: [
        WORKFLOW_STATES.APPROVED,
        WORKFLOW_STATES.REVISION_REQUESTED
      ],
      [WORKFLOW_STATES.APPROVED]: [
        WORKFLOW_STATES.TRANSITIONING_STEP,
        WORKFLOW_STATES.STEP_COMPLETED,
        WORKFLOW_STATES.ANALYSIS_COMPLETED
      ],
      [WORKFLOW_STATES.REVISION_REQUESTED]: [
        WORKFLOW_STATES.REVISING,
        WORKFLOW_STATES.ERROR
      ],
      [WORKFLOW_STATES.REVISING]: [
        WORKFLOW_STATES.REVISION_POLLING,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.RATE_LIMITED
      ],
      [WORKFLOW_STATES.REVISION_POLLING]: [
        WORKFLOW_STATES.REVIEWING,
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.RATE_LIMITED
      ],
      [WORKFLOW_STATES.TRANSITIONING_STEP]: [
        WORKFLOW_STATES.PROCESSING,
        WORKFLOW_STATES.WAITING_NEXT_STEP,
        WORKFLOW_STATES.ERROR
      ],
      [WORKFLOW_STATES.WAITING_NEXT_STEP]: [
        WORKFLOW_STATES.PROCESSING,
        WORKFLOW_STATES.IDLE
      ],
      [WORKFLOW_STATES.STEP_COMPLETED]: [
        WORKFLOW_STATES.TRANSITIONING_STEP,
        WORKFLOW_STATES.ANALYSIS_COMPLETED
      ],
      [WORKFLOW_STATES.RATE_LIMITED]: [
        WORKFLOW_STATES.PROCESSING,
        WORKFLOW_STATES.POLLING,
        WORKFLOW_STATES.ERROR
      ],
      [WORKFLOW_STATES.TIMEOUT]: [
        WORKFLOW_STATES.ERROR,
        WORKFLOW_STATES.PROCESSING
      ],
      [WORKFLOW_STATES.ERROR]: [
        WORKFLOW_STATES.IDLE,
        WORKFLOW_STATES.PROCESSING,
        WORKFLOW_STATES.REVIEWING
      ],
      [WORKFLOW_STATES.PAUSED]: [
        WORKFLOW_STATES.PROCESSING,
        WORKFLOW_STATES.IDLE
      ]
    };

    // Define state metadata
    this.stateMetadata = {
      [WORKFLOW_STATES.IDLE]: {
        displayName: 'Ready',
        canCancel: false,
        showProgress: false
      },
      [WORKFLOW_STATES.PROCESSING]: {
        displayName: 'Processing',
        canCancel: true,
        showProgress: true
      },
      [WORKFLOW_STATES.REVIEWING]: {
        displayName: 'Review Required',
        canCancel: false,
        showProgress: false
      },
      [WORKFLOW_STATES.REVISING]: {
        displayName: 'Applying Feedback',
        canCancel: false,
        showProgress: true
      },
      [WORKFLOW_STATES.ERROR]: {
        displayName: 'Error Occurred',
        canCancel: false,
        showProgress: false
      }
    };
  }

  /**
   * Check if a transition is valid
   */
  canTransition(toState) {
    // Handle undefined or null states
    if (!this.currentState || !toState) {
      return false;
    }

    // Normalize state to uppercase for comparison (only if they're strings)
    const normalizedCurrent = typeof this.currentState === 'string' ? this.currentState.toUpperCase() : this.currentState;
    const normalizedTo = typeof toState === 'string' ? toState.toUpperCase() : toState;

    // Check transitions with both cases for compatibility
    let validTransitions = this.transitions[this.currentState] || [];
    if (validTransitions.length === 0) {
      validTransitions = this.transitions[normalizedCurrent] || [];
    }

    return validTransitions.includes(toState) || validTransitions.includes(normalizedTo);
  }

  /**
   * Perform state transition with validation
   */
  transition(toState, context = {}) {
    // Handle undefined current state - set to IDLE
    if (!this.currentState) {
      console.warn('Current state is undefined, initializing to IDLE');
      this.currentState = WORKFLOW_STATES.IDLE;
    }

    // Special case: Allow staying in the same state with context update
    if (toState === this.currentState) {
      console.log(`Updating context while staying in ${this.currentState}`, context);

      // Update context
      this.context = {
        ...this.context,
        ...context,
        lastTransition: Date.now()
      };

      return {
        success: true,
        previousState: this.currentState,
        currentState: this.currentState,
        context: this.context
      };
    }

    if (!this.canTransition(toState)) {
      console.error(`Invalid transition from ${this.currentState} to ${toState}`);
      return {
        success: false,
        error: `Cannot transition from ${this.currentState} to ${toState}`,
        currentState: this.currentState
      };
    }

    // Store previous state
    this.previousState = this.currentState;

    // Update current state
    this.currentState = toState;

    // Update context
    this.context = {
      ...this.context,
      ...context,
      lastTransition: Date.now()
    };

    // Add to history
    this.stateHistory.push({
      state: toState,
      timestamp: Date.now(),
      context: { ...this.context }
    });

    // Trigger any side effects
    this.handleStateEntry(toState, context);

    console.log(`State transition: ${this.previousState} → ${this.currentState}`, context);

    return {
      success: true,
      currentState: this.currentState,
      previousState: this.previousState,
      context: this.context
    };
  }

  /**
   * Handle side effects when entering a state
   */
  handleStateEntry(state, context) {
    switch (state) {
      case WORKFLOW_STATES.ERROR:
        this.context.retryCount = (this.context.retryCount || 0) + 1;
        break;

      case WORKFLOW_STATES.RATE_LIMITED:
        this.context.rateLimitedAt = Date.now();
        break;

      case WORKFLOW_STATES.STEP_COMPLETED:
        this.context.completedSteps = [
          ...(this.context.completedSteps || []),
          this.context.currentStep
        ];
        break;

      case WORKFLOW_STATES.TRANSITIONING_STEP:
        if (context.nextStep !== undefined) {
          this.context.currentStep = context.nextStep;
        }
        break;

      case WORKFLOW_STATES.PROCESSING:
      case WORKFLOW_STATES.POLLING:
        this.context.retryCount = 0; // Reset retry count on successful processing
        break;
    }
  }

  /**
   * Get current state metadata
   */
  getStateMetadata() {
    return this.stateMetadata[this.currentState] || {
      displayName: this.currentState,
      canCancel: false,
      showProgress: false
    };
  }

  /**
   * Check if the workflow is in a processing state
   */
  isProcessing() {
    const processingStates = [
      WORKFLOW_STATES.UPLOADING_DOCUMENT,
      WORKFLOW_STATES.STARTING_ANALYSIS,
      WORKFLOW_STATES.PROCESSING,
      WORKFLOW_STATES.POLLING,
      WORKFLOW_STATES.REVISING,
      WORKFLOW_STATES.REVISION_POLLING,
      WORKFLOW_STATES.TRANSITIONING_STEP
    ];
    return processingStates.includes(this.currentState);
  }

  /**
   * Check if the workflow is in an error state
   */
  isError() {
    const errorStates = [
      WORKFLOW_STATES.ERROR,
      WORKFLOW_STATES.TIMEOUT,
      WORKFLOW_STATES.RATE_LIMITED
    ];
    return errorStates.includes(this.currentState);
  }

  /**
   * Check if the workflow can be resumed
   */
  canResume() {
    const resumableStates = [
      WORKFLOW_STATES.PAUSED,
      WORKFLOW_STATES.ERROR,
      WORKFLOW_STATES.RATE_LIMITED,
      WORKFLOW_STATES.TIMEOUT
    ];
    return resumableStates.includes(this.currentState);
  }

  /**
   * Check if user interaction is required
   */
  requiresUserAction() {
    const actionStates = [
      WORKFLOW_STATES.REVIEWING,
      WORKFLOW_STATES.AWAITING_APPROVAL,
      WORKFLOW_STATES.WAITING_NEXT_STEP,
      WORKFLOW_STATES.DOCUMENT_UPLOADED
    ];
    return actionStates.includes(this.currentState);
  }

  /**
   * Reset to idle state
   */
  reset() {
    this.currentState = WORKFLOW_STATES.IDLE;
    this.previousState = null;
    this.context = {
      currentStep: 0,
      error: null,
      retryCount: 0,
      lastTransition: Date.now()
    };
    this.stateHistory = [WORKFLOW_STATES.IDLE];
  }

  /**
   * Get state history
   */
  getHistory(limit = 10) {
    return this.stateHistory.slice(-limit);
  }

  /**
   * Handle error with appropriate state transition
   */
  handleError(error, recoverable = false) {
    const errorContext = {
      error: {
        message: error.message || 'Unknown error',
        code: error.code || ERROR_CODES.UNKNOWN,
        recoverable,
        timestamp: Date.now()
      }
    };

    if (error.code === ERROR_CODES.RATE_LIMIT) {
      return this.transition(WORKFLOW_STATES.RATE_LIMITED, errorContext);
    }

    if (error.code === ERROR_CODES.TIMEOUT) {
      return this.transition(WORKFLOW_STATES.TIMEOUT, errorContext);
    }

    return this.transition(WORKFLOW_STATES.ERROR, errorContext);
  }

  /**
   * Get a human-readable status message
   */
  getStatusMessage() {
    const metadata = this.getStateMetadata();
    const step = this.context.currentStep || 0;

    switch (this.currentState) {
      case WORKFLOW_STATES.IDLE:
        return 'Ready to start analysis';

      case WORKFLOW_STATES.UPLOADING_DOCUMENT:
        return 'Uploading document...';

      case WORKFLOW_STATES.PROCESSING:
        return `Processing Step ${step + 1}...`;

      case WORKFLOW_STATES.POLLING:
        return `Waiting for analysis results...`;

      case WORKFLOW_STATES.REVIEWING:
        return `Step ${step + 1} ready for review`;

      case WORKFLOW_STATES.REVISING:
        return 'Applying your feedback...';

      case WORKFLOW_STATES.ERROR:
        return this.context.error?.message || 'An error occurred';

      case WORKFLOW_STATES.RATE_LIMITED:
        return 'Rate limited - waiting to retry...';

      case WORKFLOW_STATES.ANALYSIS_COMPLETED:
        return 'Analysis completed successfully';

      default:
        return metadata.displayName;
    }
  }
}